'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useRef, useEffect } from 'react';
import { getTestLabels } from '../../../utils/getTestLabels.js';
import style from '../Accordion.module.scss.js';

const { componentTestLabel } = getTestLabels('AccordionGroup');
const ACCORDION_CLOSING_CLASS = 'z-accordion__item--is-closing';
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=30-1964&node-type=section&t=EDysr1f69m8d1p4V-0)
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Accordion/AccordionGroup/AccordionGroup.tsx)
 *
 * @param {boolean} allowMultipleOpen - (optional) Set to `true` to allow more than one panel to be expanded at a time
 * @param {number} disableMaxWidth - (optional) Disable the accordion at a specific media query pixel width (and below) forcing all items to be open.
 * @param {number} disableMinWidth - (optional) Disable the accordion at a specific media query pixel width (and above) forcing all items to be open.
 * @param {('large' | 'small')} size - (optional) Set to `small` to apply reduced font-size of titles (defaults to `large`).
 */
const AccordionGroup = ({ allowMultipleOpen = false, children, className, disableMaxWidth, disableMinWidth, size = 'large', ...otherProps }) => {
    const groupRef = useRef(null);
    useEffect(() => {
        const detailsElements = Array.from(groupRef.current?.querySelectorAll(':scope:not(.z-accordion-group-disabled) > details:not(.z-accordion__item--disabled)') ?? []);
        if (!allowMultipleOpen) {
            // If more than one panel has been set to open by the author but allowMultipleOpen is false, only open the first
            const openedItems = detailsElements.filter((item) => item.open);
            for (let i = 1; i < openedItems.length; i++) {
                openedItems[i].open = false;
                console.warn('<AccordionGroup>: allowMultipleOpen is set to false, but there are multiple items where defaultOpen has been set to true.');
            }
        }
        function onClick(e) {
            if (allowMultipleOpen)
                return;
            const eventTargetDetailsElem = e.target.closest('details');
            if (eventTargetDetailsElem !== e.currentTarget) {
                eventTargetDetailsElem.open && e.preventDefault();
                return;
            }
            if (eventTargetDetailsElem.open) {
                return;
            }
            for (const details of detailsElements) {
                if (details !== eventTargetDetailsElem && details.open) {
                    details.classList.add(ACCORDION_CLOSING_CLASS);
                }
            }
        }
        for (const details of detailsElements) {
            details.addEventListener('click', onClick);
        }
        return () => {
            for (const details of detailsElements) {
                details.removeEventListener('click', onClick);
            }
        };
    }, [allowMultipleOpen]);
    return (jsxs("section", { role: "group", ref: groupRef, "data-zta": componentTestLabel, "data-disable-max-width": typeof disableMaxWidth !== 'undefined' ? disableMaxWidth : undefined, "data-disable-min-width": typeof disableMinWidth !== 'undefined' ? disableMinWidth : undefined, ...otherProps, className: classNames(style['z-accordion-group'], style[`z-accordion-group--${size}`], className), children: [typeof disableMaxWidth !== 'undefined' && (jsx("style", { dangerouslySetInnerHTML: {
                    __html: `
            @media (max-width: ${disableMaxWidth}px) {
              .z-accordion-group[data-disable-max-width="${disableMaxWidth}"] .z-accordion__item {
                display: none;
              }
              .z-accordion-group[data-disable-max-width="${disableMaxWidth}"] .z-accordion__item--disabled {
                display: block;
              }
            }
        `,
                } })), typeof disableMinWidth !== 'undefined' && (jsx("style", { dangerouslySetInnerHTML: {
                    __html: `
            @media (min-width: ${disableMinWidth}px) {
              .z-accordion-group[data-disable-min-width="${disableMinWidth}"] .z-accordion__item {
                display: none;
              }
              .z-accordion-group[data-disable-min-width="${disableMinWidth}"] .z-accordion__item--disabled {
                display: block;
              }
            }
        `,
                } })), children] }));
};

export { AccordionGroup };
//# sourceMappingURL=AccordionGroup.js.map
